import {
  Button,
  makeStyles,
  createStyles,
  Tooltip,
} from "@material-ui/core";
import React, { useEffect, useMemo } from "react";
import AddIcon from "@material-ui/icons/Add";
import { useDialogProps } from "legacy-components/dialog/Dialog";
import { useAsyncAction } from "modus/components/form/async-actions/AsyncAction";
import CreateDeploymentDialog from "../../components/CreateDeploymentDialog";
import {
  hasAvailableRegionalDeploymentSlot,
  isGlobalAlreadyDeployed,
  regionItemsToRegionGroups,
} from "../../config/deploymentRegions";
import { ApiRegionsService } from "../../services/ApiService";
import { FLATTENED_API_DEPLOYMENT_DISABLED_MESSAGE } from "./deploymentCreatorTypes";
import type { DeploymentCreatorProps } from "./deploymentCreatorTypes";

const DEPLOYMENT_HEADER_CONTROL_HEIGHT = 40;

const useStyles = makeStyles((theme) =>
  createStyles({
    addButton: {
      height: DEPLOYMENT_HEADER_CONTROL_HEIGHT,
      minHeight: DEPLOYMENT_HEADER_CONTROL_HEIGHT,
      padding: theme.spacing(0, 2),
      "&.Mui-disabled": {
        opacity: 1,
        backgroundColor: theme.palette.grey[300],
        color: theme.palette.grey[600],
      },
      "&.Mui-disabled .MuiButton-startIcon": {
        color: theme.palette.grey[500],
      },
    },
  })
);

export const DeploymentCreatorRegional: React.FC<DeploymentCreatorProps> = ({
  resourceAppId,
  version,
  refreshDeploymentList,
  deployedRegions,
  basePath,
  onDeploymentAdded,
  isFlattenedApi = false,
}) => {
  const { open, toggle } = useDialogProps();
  const { state: regionsState, subscribe: fetchRegions } = useAsyncAction(() =>
    ApiRegionsService.getRegions()
  );

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions]);

  const regionGroups = useMemo(() => {
    const items = regionsState?.value;
    if (!Array.isArray(items)) return [];
    return regionItemsToRegionGroups(items);
  }, [regionsState?.value]);

  const regionsCatalog = useMemo(() => {
    const items = regionsState?.value;
    return Array.isArray(items) ? items : [];
  }, [regionsState?.value]);

  const onSuccess = () => {
    refreshDeploymentList();
    toggle();
    onDeploymentAdded?.();
  };

  const isFedRamp = process.env.REACT_APP_IS_FEDRAMP === "true";
  const canAddDeployment = isFedRamp
    ? !deployedRegions || deployedRegions.length === 0
    : hasAvailableRegionalDeploymentSlot(deployedRegions, regionsCatalog) || !isGlobalAlreadyDeployed(deployedRegions);
  
  const addDeploymentDisabled = !canAddDeployment || isFlattenedApi;

  const classes = useStyles();

  const addButton = (
    <Button
      disabled={addDeploymentDisabled}
      size="medium"
      variant="contained"
      color="primary"
      className={classes.addButton}
      startIcon={<AddIcon />}
      onClick={() => toggle()}
    >
      Add New Deployment
    </Button>
  );

  return (
    <>
      {isFlattenedApi ? (
        <Tooltip
          title={FLATTENED_API_DEPLOYMENT_DISABLED_MESSAGE}
          placement="top"
          arrow
        >
          <span style={{ display: "inline-flex" }}>{addButton}</span>
        </Tooltip>
      ) : (
        addButton
      )}
      <CreateDeploymentDialog
        open={open}
        onClose={toggle}
        onSuccess={onSuccess}
        basePath={basePath}
        resourceAppId={resourceAppId}
        version={version}
        regionGroups={regionGroups}
        deployedRegions={deployedRegions}
      />
    </>
  );
};
